import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, waitForAnimationEnd } from '@theme/utilities';

/**
 * CYGNUSS 30/10/2026: marca posta no details do menu no toque de abrir. Com ela o ouvinte de toggle do dialog.js nao
 * trava a pagina (o toggle chega em hora incerta, as vezes antes da primeira pintura da gaveta): este componente trava
 * uma vez so, logo depois dela.
 */
const TRAVA_DEPOIS = 'data-cyg-trava-depois';

/**
 * CYGNUSS 30/10/2026: sem :focus-visible (iOS < 15.4) o tema desenha o anel em todo :focus (base.css), entao o foco
 * nao e levado para dentro do menu ao abrir (a aparencia ficaria diferente da de antes).
 */
const FOCO_VISIVEL = (() => {
  try {
    return CSS.supports('selector(:focus-visible)');
  } catch (erro) {
    return false;
  }
})();

/**
 * CYGNUSS 30/10/2026: chave de medida. Com html[data-cyg-gaveta="antigo"] o menu segue o caminho de antes (espera por
 * getAnimations, trava pelo toggle do dialog.js e foco fora do menu), para comparar na mesma pagina.
 * @returns {boolean}
 */
function caminhoAntigo() {
  return document.documentElement.dataset.cygGaveta === 'antigo';
}

/**
 * CYGNUSS 30/10/2026: vez de cada details. Cada abrir e cada fechar soma 1, e o que roda depois (o rAF e a trava da
 * abertura, o fim da saida) so age se a vez ainda e a dele. Um fechar que chega antes do rAF da abertura (toque duplo
 * com a CPU lenta, o fundo ja recebe toque assim que o details abre) cancela a abertura em vez de ser ignorado, e o
 * menu termina fechado, com o botao dizendo recolhido. Um abrir durante a saida cancela a saida, como antes.
 * @type {WeakMap<HTMLDetailsElement, number>}
 */
const vezes = new WeakMap();

/**
 * @param {HTMLDetailsElement} details
 * @returns {number} a vez nova desse details
 */
function novaVez(details) {
  const vez = (vezes.get(details) || 0) + 1;
  vezes.set(details, vez);
  return vez;
}

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  #desktopMenuQuery = matchMedia('(min-width: 1024px)');
  #lastMenuFocus = null;

  // CSS can hide the focused drawer before the desktop media-change event arrives.
  #onMenuFocus = (event) => {
    if (!this.isOpen || !this.matches('.header__drawer--mobile') || !this.querySelector('.cmm')) return;
    const { details } = this.refs;
    if (event.type === 'focusin') {
      this.#lastMenuFocus = details.contains(event.target) ? event.target : null;
    } else if (details.contains(event.target)) {
      const movedOutside = event.relatedTarget && !details.contains(event.relatedTarget);
      const hiddenOnDesktop = this.#desktopMenuQuery.matches && !this.#isVisible(event.target);
      if (movedOutside || !hiddenOnDesktop) this.#lastMenuFocus = null;
    }
  };

  // The mobile drawer also serves tablets; release its focus and scroll lock when desktop navigation returns.
  #onDesktopMenuChange = ({ matches }) => {
    if (matches && this.matches('.header__drawer--mobile') && this.isOpen) {
      const { details } = this.refs;
      const restoreHiddenFocus = document.activeElement === document.body &&
        this.#lastMenuFocus && details.contains(this.#lastMenuFocus) &&
        !this.#isVisible(this.#lastMenuFocus) && !this.#isVisible(details.querySelector('summary'));
      this.#close(details, Boolean(restoreHiddenFocus));
    }
  };

  // This listener precedes the native trap, whose candidate list includes hidden menu panes.
  #onMenuTab = (event) => {
    if (event.key !== 'Tab' || !this.isOpen || !this.matches('.header__drawer--mobile') || !this.querySelector('.cmm')) return;

    const focusable = Array.from(this.refs.details.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )).filter((element) => this.#isVisible(element) && !element.closest('[hidden], [inert]'));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === this.refs.details)) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  #isVisible(element) {
    return element instanceof HTMLElement && element.getClientRects().length > 0 &&
      !['hidden', 'collapse'].includes(getComputedStyle(element).visibility);
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    document.addEventListener('keydown', this.#onMenuTab, true);
    document.addEventListener('focusin', this.#onMenuFocus, true);
    document.addEventListener('focusout', this.#onMenuFocus, true);
    this.#desktopMenuQuery.addEventListener('change', this.#onDesktopMenuChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    document.removeEventListener('keydown', this.#onMenuTab, true);
    document.removeEventListener('focusin', this.#onMenuFocus, true);
    document.removeEventListener('focusout', this.#onMenuFocus, true);
    this.#lastMenuFocus = null;
    this.#desktopMenuQuery.removeEventListener('change', this.#onDesktopMenuChange);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (details === this.refs.details && this.matches('.header__drawer--mobile') && this.querySelector('.cmm')) {
      this.#lastMenuFocus = details.contains(document.activeElement) ? document.activeElement : null;
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    if (caminhoAntigo()) {
      requestAnimationFrame(() => {
        details.classList.add('menu-open');

        // Wait for the drawer animation to complete before trapping focus
        const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
        onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
      });
      return;
    }

    // CYGNUSS 30/10/2026: menu do celular sem trabalho a mais no quadro do toque (etapa 8, item 4). A mesma classe e a
    // mesma animacao de antes, mas a espera pelo fim dela e pelos eventos do proprio elemento animado
    // (waitForAnimationEnd, sem getAnimations, que forcava o recalculo do estilo da pagina no meio do quadro). A trava de
    // rolagem e o foco ficam para depois da primeira pintura (setTimeout dentro do rAF): a trava uma vez so (o dialog.js
    // nao trava mais no toggle quando ve a marca) e o foco no X do menu, sem rolar e sem anel quando a abertura foi pelo
    // toque. Se o X ainda nao pode receber foco nesse momento (gaveta no comeco da entrada), tenta de novo depois do
    // quadro seguinte e, por ultimo, no fim da entrada.
    const principal = details === this.refs.details;
    const travaDepois = principal && details.hasAttribute('scroll-lock');
    // CYGNUSS 30/10/2026: o foco so entra no menu quando o fechar tambem o devolve ao botao (menu do celular com o menu
    // Cygnuss, a mesma condicao do restoreMenuFocus em #close). Sem isso, o foco ficaria preso num menu fechado.
    const devolveFoco = principal && this.#devolveFoco();
    const opcoesDoFoco = devolveFoco ? this.#opcoesDoFoco() : null;
    const vez = novaVez(details);
    const minhaVez = () => vezes.get(details) === vez;

    if (travaDepois) details.setAttribute(TRAVA_DEPOIS, '');

    requestAnimationFrame(() => {
      if (!minhaVez()) {
        if (travaDepois) details.removeAttribute(TRAVA_DEPOIS);
        return;
      }

      details.classList.add('menu-open');

      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      const fim = waitForAnimationEnd(drawer || details);

      if (!principal) {
        fim.then(() => {
          if (minhaVez() && details.classList.contains('menu-open')) trapFocus(details);
        });
        return;
      }

      const focar = () =>
        minhaVez() && details.open && details.classList.contains('menu-open')
          ? this.#focarDentro(details, summary, opcoesDoFoco)
          : true;

      setTimeout(() => {
        if (travaDepois) details.removeAttribute(TRAVA_DEPOIS);
        if (!minhaVez() || !details.open || !details.classList.contains('menu-open')) return;

        trapFocus(details);
        const focou = focar();

        if (travaDepois && !document.documentElement.hasAttribute('scroll-lock')) {
          document.documentElement.setAttribute('scroll-lock', '');
        }

        // No primeiro quadro a gaveta ainda esta no ponto de partida da entrada (visibility: hidden), e o navegador
        // recusa o foco: tenta de novo depois do quadro seguinte e, se ainda nao der, no fim da entrada.
        if (!focou) {
          requestAnimationFrame(() =>
            setTimeout(() => {
              if (!focar()) fim.then(focar);
            }, 0)
          );
        }
      }, 0);
    });
  }

  /**
   * CYGNUSS 30/10/2026: menu do celular com o menu Cygnuss, o unico em que o fechar devolve o foco ao botao do menu.
   * @returns {boolean}
   */
  #devolveFoco() {
    return this.matches('.header__drawer--mobile') && Boolean(this.querySelector('.cmm'));
  }

  /**
   * CYGNUSS 30/10/2026: como focar sem mudar a aparencia. Sempre sem rolar a pagina; quando quem tem o foco agora nao
   * mostra o anel (toque ou clique), o novo foco tambem nao mostra. Pelo teclado, o navegador mostra o anel como antes.
   * Sem suporte a :focus-visible, null: o foco nao e movido.
   * @returns {FocusOptions | null}
   */
  #opcoesDoFoco() {
    if (!FOCO_VISIVEL) return null;
    const ativo = document.activeElement;
    const peloTeclado = ativo instanceof Element && ativo !== document.body && ativo.matches(':focus-visible');
    return peloTeclado ? { preventScroll: true } : /** @type {FocusOptions} */ ({ preventScroll: true, focusVisible: false });
  }

  /**
   * CYGNUSS 30/10/2026: leva o foco para o X do menu (ou o primeiro botao ou link dele), so enquanto o foco ainda esta no
   * botao do menu ou solto na pagina. Se a cliente ja foi para outro controle, ou outra janela pegou o foco, fica onde esta.
   * @param {HTMLDetailsElement} details
   * @param {HTMLElement} summary
   * @param {FocusOptions | null} opcoes
   * @returns {boolean} true quando nao ha mais nada a fazer (foco dentro do menu, ou ele nao deve ser movido)
   */
  #focarDentro(details, summary, opcoes) {
    const drawer = details.querySelector(':scope > .menu-drawer');
    if (!opcoes || !drawer) return true;

    const ativo = document.activeElement;
    if (ativo && drawer.contains(ativo)) return true;
    if (ativo && ativo !== document.body && ativo !== summary) return true;

    const alvo =
      drawer.querySelector(':scope > .menu-drawer__close-button') || drawer.querySelector('a[href], button:enabled');
    if (!(alvo instanceof HTMLElement)) return true;

    alvo.focus(opcoes);
    return document.activeElement === alvo;
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details, restoreHiddenFocus = false) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    const restoreMenuFocus = details === this.refs.details && this.#devolveFoco() &&
      (details.contains(document.activeElement) || restoreHiddenFocus);

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    /** @param {FocusOptions} [focusOptions] */
    const finish = (focusOptions) => {
      reset(details);
      if (details === this.refs.details) {
        this.#lastMenuFocus = null;
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
        // Do not reclaim focus if the customer moved to another control during the closing animation.
        if (restoreMenuFocus && (document.activeElement === document.body || details.contains(document.activeElement))) {
          const target = this.#isVisible(summary) ? summary : this.closest('.header')?.querySelector('.header-logo');
          if (this.#isVisible(target)) target.focus(focusOptions);
        }
      } else {
        trapFocus(this.refs.details);
      }
    };

    if (caminhoAntigo()) {
      onAnimationEnd(drawer || details, () => finish(), { subtree: false });
      return;
    }

    // CYGNUSS 30/10/2026: a mesma saida de antes, esperada pelos eventos do proprio elemento animado (waitForAnimationEnd,
    // sem getAnimations no meio do toque). O foco volta ao botao do menu sem rolar a pagina e sem anel quando o fechar foi
    // pelo toque. Se o menu abriu de novo durante a saida, nada e desfeito (a vez mudou). Um abrir que ainda nao poe
    // menu-open (rAF pendente) nao conta como reabertura: ele mesmo desiste ao ver a vez deste fechar.
    const vez = novaVez(details);
    const focusOptions = this.#opcoesDoFoco() ?? undefined;
    waitForAnimationEnd(drawer || details).then(() => {
      if (vezes.get(details) !== vez) return;
      finish(focusOptions);
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
